import { useLayoutEffect, useRef } from 'react';

import ActionIcon from './ActionIcon';
import PlatformIcon from './PlatformIcon';
import { money, questionLabel, shortDateTime, stateLabel } from '../lib/format';
import { absoluteThreadUrl, DUPLICATE_ALERT, isOpen, priorityReasons } from '../lib/queue';
import type { Draft } from '../lib/types';

/**
 * One draft, and the whole job: read the reply, open the thread, post it by hand - or hand it to
 * the local poster - and say so.
 *
 * Three things about the interaction are deliberate.
 *
 * 1. The next step is always the loudest button, and the colour says which one it is. Before you
 *    have opened the thread that is "Copy → Open", solid; once you have, "Posted" turns solid
 *    green and the copy button demotes itself to a quiet outline. Nothing here asks you to work
 *    out the order, and every button is a verb with a mark beside it rather than a phrase.
 *
 * 2. Posting is confirmed in the card, never in a `window.confirm`. A native dialog is unstyled,
 *    can be suppressed by the browser, cannot be undone, and made a five-second decision cost a
 *    modal. The card also knows something a dialog cannot: whether you actually opened this
 *    thread in this session. If you did, one click records it. If you did not - you replied from
 *    your phone, say - the card asks first.
 *
 * 3. A card ends in exactly three ways: posted, queued for the local poster, or removed. Remove
 *    sits next to Posted because that is the choice being made, and it is one click with an Undo
 *    in the toast - it is a decision about a draft, not a deletion of the stranger's post. Auto
 *    post (7 Oct 2026) is the machine's ending: it queues the reply for `npm run post:fb` rather
 *    than sending anything from here, so the button says "queued", never "posted".
 *
 * 4. "Update reply" rewrites the text instead of deciding what to do with it, so it sits under
 *    the reply and apart from the row of outcome buttons - see the note beside it.
 */

interface Props {
  row: Draft;
  /** Current editor text: local edit, else the saved edit, else the generated reply. */
  body: string;
  /** Set when the thread was opened from this card in this session. */
  armed: boolean;
  armedAt: string | null;
  confirming: boolean;
  busy: boolean;
  onEdit: (value: string) => void;
  onSaveEdit: () => void;
  onOpen: () => void;
  onAskPost: () => void;
  onCancelPost: () => void;
  onPost: () => void;
  /** Queue this reply for the local poster. It queues; it does not send. */
  onQueueAutoPost: () => void;
  /** Take a queued reply back out. Nothing has been sent yet. */
  onCancelAutoPost: () => void;
  /** Write this reply again with the prompts currently saved in Setup → Reply prompts. */
  onRedraft: () => void;
  /** True while that rewrite is in flight, so the button can say so instead of looking stuck. */
  redrafting: boolean;
  /** Take this draft out of the queue. Undoable, and it moves to the Rejected view. */
  onRemove: () => void;
  onReopen: () => void;
}

export default function DraftCard({
  row,
  body,
  armed,
  armedAt,
  confirming,
  busy,
  onEdit,
  onSaveEdit,
  onOpen,
  onAskPost,
  onCancelPost,
  onPost,
  onQueueAutoPost,
  onCancelAutoPost,
  onRedraft,
  redrafting,
  onRemove,
  onReopen,
}: Props) {
  const editorRef = useRef<HTMLTextAreaElement | null>(null);

  const prospect = row.prospect;
  const item = prospect?.item;
  const source = item?.source ?? null;
  const threadUrl = absoluteThreadUrl(item?.platform, item?.permalink ?? item?.url);
  const actionable = isOpen(row.state);

  const kbRefs = Array.isArray(row.kb_refs) ? row.kb_refs : [];

  const edited = row.edited_body !== null && row.edited_body.trim() !== row.body.trim();
  const duplicate =
    row.duplicate_similarity !== null && row.duplicate_similarity >= DUPLICATE_ALERT;
  const reasons = priorityReasons(row);
  /** Waiting for the local poster rather than for a human. One writer: the button below. */
  const queued = row.state === 'approved';

  /**
   * Grow the editor to fit its content.
   *
   * This used to run from an inline `ref` callback, which React calls twice per render (null,
   * then the element), and each call read `scrollHeight` - a forced synchronous layout, for every
   * card, on every realtime refetch. Now it runs only when the text actually changes.
   */
  useLayoutEffect(() => {
    const element = editorRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [body]);

  const meta = [
    item?.author_handle ?? null,
    `intent ${prospect?.intent_score ?? '?'}/100`,
    questionLabel(prospect?.question_type) || null,
    item?.posted_at ? `posted ${shortDateTime(item.posted_at)}` : null,
  ].filter(Boolean);

  return (
    <article
      className="draft-card"
      aria-label={`Draft for ${source?.display_name ?? source?.identifier ?? 'an unknown source'}`}
    >
      <div className="draft-head">
        <div>
          <h3 className="source-head">
            {/* The platform as a mark rather than a word: this list mixes Reddit, Facebook and
                Instagram on purpose, and which one it is should be visible without reading. */}
            <PlatformIcon platform={item?.platform} />
            <span>{source?.display_name ?? source?.identifier ?? 'unknown source'}</span>
          </h3>
          <div className="small">
            {/* Why this draft is where it is. The queue has always been sorted by a deliberate
                opinion (lib/queue.ts) and has never said so out loud. */}
            {reasons.length > 0 ? <b>{reasons.join(' · ')} · </b> : null}
            <span className="muted">{meta.join(' · ')}</span>
          </div>
        </div>

        <div className="badges">
          {row.state !== 'pending' ? (
            <span className={row.state === 'approved' ? 'badge ok' : 'badge'}>
              {stateLabel(row.state)}
            </span>
          ) : null}
          {duplicate ? (
            <span className="badge warn" title="Similarity against a reply already sent">
              maybe a duplicate {Number(row.duplicate_similarity).toFixed(2)}
            </span>
          ) : null}
          {edited ? <span className="badge">edited</span> : null}
        </div>
      </div>

      <blockquote className="thread">
        {item?.title ? <strong>{item.title}</strong> : null}
        {item?.title ? '\n\n' : ''}
        {item?.body ?? '(no body stored)'}
      </blockquote>

      {actionable ? (
        <textarea
          aria-label="Draft reply"
          value={body}
          ref={editorRef}
          onChange={(event) => onEdit(event.target.value)}
          onBlur={onSaveEdit}
          onKeyDown={(event) => {
            // Save on Ctrl/Cmd+Enter and record the post in one keystroke.
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              onSaveEdit();
              onPost();
            }
          }}
          spellCheck
        />
      ) : (
        <blockquote className="thread">{body}</blockquote>
      )}

      {/*
        "Update reply" belongs to the reply, not to the row of outcome buttons below: it is the
        only control on this card that changes the text above it, and the row below is the
        decision about whether to post that text. Mixing a rewrite in with Copy → Open and Posted
        would put "write it differently" next to "this went out".

        Before this existed, a prompt edit in Setup only reached drafts that had not been written
        yet - so judging an edit meant waiting for the next post to arrive, and there was no way
        to ask "is this wording better?" about the reply in front of you.
      */}
      {actionable ? (
        <div className="reply-tools">
          <button type="button" className="secondary" disabled={busy || redrafting} onClick={onRedraft}>
            <ActionIcon name="refresh" />
            {redrafting ? 'Updating…' : 'Update reply'}
          </button>
          <span className="muted small">
            {redrafting
              ? 'Writing this reply again with the prompts saved in Setup.'
              : 'Writes this reply again with the prompts saved in Setup. Your edit here is replaced.'}
          </span>
        </div>
      ) : null}

      {/* Only failures about this reply. "Not sendable yet" used to head this line for the kill
          switch, which is on by default and stops the *system* sending - something this dashboard
          does not do in any case, and no obstacle to posting it here by hand. */}
      {queued ? (
        <p className="status muted">
          Queued for auto post — the local poster sends it on its next run ({' '}
          <code>npm run post:fb</code> ), and the badge above says where it has got to.
        </p>
      ) : null}
      {!actionable && row.state === 'rejected' ? (
        <p className="status muted">Rejected: {row.rejected_reason ?? 'no reason recorded'}</p>
      ) : null}

      {actionable ? (
        <>
          <div className="row-actions">
            <button
              type="button"
              className={armed ? 'secondary' : undefined}
              disabled={busy || !threadUrl}
              onClick={onOpen}
            >
              <ActionIcon name="copy" />
              Copy → Open
            </button>
            {/* The same thread, the other way: read it here and reply by hand, with the clipboard
                left alone. Next to the button it alternates with, and wearing the same clothes as
                its neighbours - an anchor styled as a button (a.buttonish) so middle-click and
                "open in new tab" still work, which a real <button> would have thrown away. */}
            <a
              href={threadUrl || undefined}
              target="_blank"
              rel="noreferrer noopener"
              className="buttonish secondary"
              aria-disabled={busy || !threadUrl ? 'true' : undefined}
            >
              <ActionIcon name="open" />
              Open
            </a>
            {/* The machine's ending (7 Oct 2026): queue this reply for the local poster, which
                sends it from the operator's PC. Deliberately next to Posted rather than beside
                Copy - it is an answer to "what happens to this reply", not a way to read it -
                and deliberately never green: nothing has gone out yet. Once queued, the same
                button takes it back out, and the status line above says what queueing means. */}
            <button
              type="button"
              className="secondary"
              disabled={busy || !threadUrl}
              onClick={queued ? onCancelAutoPost : onQueueAutoPost}
            >
              <ActionIcon name="send" />
              {queued ? 'Cancel auto post' : 'Auto post'}
            </button>
            {/* Green, and solid the moment the thread has been opened in this session: this is the
                only affirmative ending a card has, so it is the one thing on it that is allowed to
                look like an outcome rather than an offer. */}
            <button
              type="button"
              className={armed ? 'post ready' : 'post'}
              disabled={busy}
              onClick={onAskPost}
            >
              <ActionIcon name="posted" />
              Posted
            </button>
            {/* Next to the post action on purpose: it is the other way this card ends, and it
                is one click - the toast that follows has an Undo, and the draft moves to the
                Rejected view rather than disappearing. */}
            <button type="button" className="secondary remove" disabled={busy} onClick={onRemove}>
              <ActionIcon name="remove" />
              Remove
            </button>
          </div>

          {armed && !confirming ? (
            <p className="muted small" style={{ marginTop: 10 }}>
              Opened {shortDateTime(armedAt)} — the reply is on your clipboard. Paste it, post it,
              then click Posted.
            </p>
          ) : null}

          {confirming ? (
            <div className="armed-line">
              <span>
                {armed
                  ? 'Did you post it in that thread?'
                  : 'You have not opened this thread in this session — only confirm if you posted it yourself.'}
              </span>
              <span className="grow" />
              <button type="button" className="post ready" disabled={busy} onClick={onPost}>
                <ActionIcon name="posted" />
                Yes, posted
              </button>
              <button type="button" className="link" disabled={busy} onClick={onCancelPost}>
                Cancel
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="quiet-actions">
          {row.state === 'sent' ? (
            // Not an undo: a delivery row exists, and the caps, the min-gap rule and the survival
            // check all read it. Correcting that is a deliberate act, not a button next to it.
            <span className="muted small">
              Posted — recorded as a delivery, so it cannot be un-posted from here.
            </span>
          ) : (
            <button type="button" className="secondary" disabled={busy} onClick={onReopen}>
              Put back in the queue
            </button>
          )}
          <a
            href={threadUrl || undefined}
            target="_blank"
            rel="noreferrer noopener"
            className="muted small"
          >
            Open thread
          </a>
        </div>
      )}

      {/* Everything a reviewer needs occasionally and never at a glance. */}
      <details className="details">
        <summary>Details</summary>
        <div className="provenance">
          <div>
            {row.model} · prompt {row.prompt_version} · {row.tokens_in ?? 0} in / {row.tokens_out ?? 0}{' '}
            out · {money(row.cost_gbp)} · {shortDateTime(row.created_at)}
          </div>
          <div>
            draft {row.id.slice(0, 8)} · prospect {prospect?.id.slice(0, 8) ?? '—'} · item{' '}
            {item?.id.slice(0, 8) ?? '—'}
            {row.duplicate_similarity !== null
              ? ` · duplicate similarity ${Number(row.duplicate_similarity).toFixed(4)}`
              : ''}
          </div>
          {kbRefs.length > 0 ? <div>KB rows used: {kbRefs.join(', ')}</div> : null}
          {prospect?.classifier_reason ? <div>Classifier: {prospect.classifier_reason}</div> : null}
        </div>
      </details>
    </article>
  );
}
